/**
 * Inspector mode state (TEC-465 P2/P3).
 *
 * Zero-overhead contract: when inspector mode is off, `<Msg>` reads `enabled`
 * from this context (a single boolean) and renders the message untouched — no
 * DOM markers, no usage recording, no observers. When there is no provider at
 * all (the normal production tree), `useI18nInspector()` returns a frozen
 * DISABLED constant, so the cost is one context read and a falsy branch.
 *
 * Inspector mode only becomes *available* for reviewers (gated upstream by the
 * `available` prop, which the host computes from its auth + a kill flag), and
 * only becomes *enabled* when a reviewer toggles it on. It can be killed
 * instantly by forcing `available={false}`.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'i18n-inspector-enabled';
const FOCUS_QUERY_PARAM = 'i18nInspect';

export interface InspectorState {
  /** Reviewer + not killed: the toggle may be shown. */
  available: boolean;
  /** available && toggled on: markers render and edits are possible. */
  enabled: boolean;
  /** Message ID to focus, from a `?i18nInspect=` deep link (catalogue → context). */
  focusId: string | null;
  setEnabled: (on: boolean) => void;
  setFocusId: (id: string | null) => void;
}

const DISABLED: InspectorState = {
  available: false,
  enabled: false,
  focusId: null,
  setEnabled: () => {},
  setFocusId: () => {},
};

const InspectorContext = createContext<InspectorState | undefined>(undefined);

export function InspectorProvider({
  available,
  children,
}: {
  available: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const [toggled, setToggled] = useState(false);
  const [focusId, setFocusId] = useState<string | null>(null);

  useEffect(() => {
    if (!available) {
      setToggled(false);
      return;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      const focus = params.get(FOCUS_QUERY_PARAM);
      if (focus) {
        // Arriving from a catalogue "Open in context" deep link: focus + auto-on.
        setFocusId(focus);
        setToggled(true);
        return;
      }
      if (localStorage.getItem(STORAGE_KEY) === '1') setToggled(true);
    } catch {
      /* localStorage / URL unavailable — stay off. */
    }
  }, [available]);

  const setEnabled = (on: boolean): void => {
    setToggled(on);
    try {
      localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const enabled = available && toggled;
  const value = useMemo<InspectorState>(
    () => ({ available, enabled, focusId, setEnabled, setFocusId }),
    [available, enabled, focusId],
  );

  return <InspectorContext.Provider value={value}>{children}</InspectorContext.Provider>;
}

export function useI18nInspector(): InspectorState {
  return useContext(InspectorContext) ?? DISABLED;
}
